import type {
  Approach,
  Question,
  ReviewResult,
  TaskItem,
} from '../../api/types';
import { chatHandlers } from './streamUiChatHandlers';
import { taskHandlers } from './streamUiTaskHandlers';

export { appendStreamingContent } from './streamingContent';

interface ActiveSlotState {
  content: string;
  buffer: string;
  slot: number;
  totalSlots: number;
}

interface RateLimitInfo {
  message: string;
  resetTime?: string;
  timezone?: string;
}

export interface DiscoveredTaskSummary {
  taskNumber: string;
  title: string;
  dependsOn?: string[];
}

export interface RunningTaskOptions {
  slot?: number;
  totalSlots?: number;
}

export interface StreamUiState {
  isStreaming: boolean;
  streamingContent: string;
  pendingQuestions?: Question[];
  pendingApproaches?: { question: string; approaches: Approach[] };
  rateLimitInfo?: RateLimitInfo;
  /**
   * Aviso de que a fase roda em `auto` porque a conta Copilot não libera o
   * modelo escolhido (copilot-modelos-da-conta). Limpo a cada `chat_start`: o
   * worker reemite o aviso em todo turno.
   */
  modelFallbackNotice?: string;
  /** True after receiving phase_complete/done (confirmed completion). */
  phaseCompleted: boolean;
  /** True after the first chat_start dispatch in this session. */
  everStartedStreaming: boolean;
  /** True between chat_start and the first text chunk arriving. */
  isThinking: boolean;
  tasks: TaskItem[];
  executionState: 'idle' | 'running' | 'paused';
  isTaskStreaming: boolean;
  taskStreamingContent: string;
  streamingTaskNumber: string | null;
  taskStreamBuffer: string;
  reviewResult?: ReviewResult;
  isReviewLoading: boolean;
  /** Parallel execution: map of taskNumber -> slot streaming state */
  activeTaskSlots: Record<string, ActiveSlotState>;
}

export type StreamUiAction =
  | { type: 'chat_start' }
  | { type: 'chat_resume_stream' }
  | { type: 'chat_complete' }
  | { type: 'chat_append'; content: string }
  | { type: 'chat_set_questions'; questions: Question[]; content: string }
  | { type: 'chat_stop' }
  | { type: 'chat_interrupt' }
  | {
      type: 'chat_rate_limit';
      content: string;
      resetTime?: string;
      timezone?: string;
    }
  | { type: 'chat_model_fallback'; content: string }
  | { type: 'chat_clear_model_fallback' }
  | { type: 'chat_clear_questions' }
  | {
      type: 'chat_set_approaches';
      approaches: { question: string; approaches: Approach[] };
      content?: string;
    }
  | { type: 'chat_clear_approaches' }
  | { type: 'phase_changed' }
  | { type: 'chat_reconnect' }
  | {
      type: 'tasks_loaded';
      tasks: TaskItem[];
      executionState: 'idle' | 'running' | 'paused';
    }
  | { type: 'task_stream_start' }
  | { type: 'task_stream_stop' }
  | {
      type: 'tasks_discovered';
      tasks: DiscoveredTaskSummary[];
    }
  | { type: 'task_started'; taskNumber: string; taskTitle?: string }
  | {
      type: 'task_progress';
      content: string;
      taskChunkType?: 'text' | 'tool_use';
    }
  | {
      type: 'qa_progress';
      content: string;
      taskChunkType?: 'text' | 'tool_use';
    }
  | { type: 'task_completed'; taskNumber: string }
  | {
      type: 'task_integrated';
      taskNumber: string;
      slot?: number;
      totalSlots?: number;
      worktreePath?: string;
    }
  | { type: 'task_error'; taskNumber?: string; errorMessage: string }
  | {
      type: 'task_parallel_start';
      taskNumber: string;
      taskTitle?: string;
      slot: number;
      totalSlots: number;
    }
  | {
      type: 'task_parallel_progress';
      taskNumber: string;
      content: string;
      taskChunkType?: 'text' | 'tool_use';
    }
  | { type: 'task_parallel_complete'; taskNumber: string }
  | { type: 'task_parallel_error'; taskNumber: string; errorMessage: string }
  | { type: 'execution_state'; executionState: 'idle' | 'running' | 'paused' }
  | { type: 'review_loading' }
  | { type: 'review_result'; result: ReviewResult };

export const initialStreamUiState: StreamUiState = {
  isStreaming: false,
  isThinking: false,
  streamingContent: '',
  pendingQuestions: undefined,
  pendingApproaches: undefined,
  reviewResult: undefined,
  isReviewLoading: false,
  rateLimitInfo: undefined,
  modelFallbackNotice: undefined,
  phaseCompleted: false,
  everStartedStreaming: false,
  tasks: [],
  executionState: 'idle',
  isTaskStreaming: false,
  taskStreamingContent: '',
  streamingTaskNumber: null,
  taskStreamBuffer: '',
  activeTaskSlots: {},
};

type StreamUiActionOf<T extends StreamUiAction['type']> = Extract<
  StreamUiAction,
  { type: T }
>;

/**
 * Um handler por ação, tipado pela própria ação. O mapa é exaustivo: ação nova
 * sem handler não compila. Os handlers vivem em `streamUiChatHandlers` (chat,
 * revisão, fase) e `streamUiTaskHandlers` (tasks, paralelo, QA).
 */
export type StreamUiHandlers = {
  [T in StreamUiAction['type']]: (
    state: StreamUiState,
    action: StreamUiActionOf<T>,
  ) => StreamUiState;
};

const STREAM_UI_HANDLERS: StreamUiHandlers = {
  ...chatHandlers,
  ...taskHandlers,
};

export function streamUiReducer(
  state: StreamUiState,
  action: StreamUiAction,
): StreamUiState {
  const handler = STREAM_UI_HANDLERS[action.type] as
    | ((state: StreamUiState, action: StreamUiAction) => StreamUiState)
    | undefined;
  return handler ? handler(state, action) : state;
}
